import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  BookingState,
  ICalendarEventType,
  NotificationIdMap,
  NotificationsType,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  PositiveIcons,
  RecurrenceActions,
  TypeOfUser,
  getClickedNotification,
  getNotification,
  getNotificationLoad,
  getNotificationState,
  getTypeOfUser,
  notificationActions,
  resetStatus,
  setClickedNotification,
  setInitialLandingURL,
  successOrSelector,
} from '@pharmaplan/common';
import { INotificationsData } from '@pharmaplan/common/models/NotificationsModel';

import { getTodayWithoutOffset } from '../components/Reports/helpers';
import { isAppLandingUrl } from '../helpers/Functions';
import { Constants } from '../helpers/Constants';

import useDrawerNavigation from './useDrawerNavigation';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import useSelfService from './useSelfService';

const { series } = RecurrenceActions;
const { pharmacist, pharmacy } = TypeOfUser;

const { requested, posted, booked: pharmacyBooked } = PharmacyLegendVariant;
const { booked } = PharmacistLegendVariant;

const statusMap = [posted, requested, booked];
const { calendarDashboard } = Constants.paths;

const initValue = {
  isRequested: false,
  item: null,
};

interface ICommonDrawerOpen {
  id: string;
  professionalId?: string;
  inType: ICalendarEventType;
  isAutoSyncRecord?: boolean;
  inBookingState: BookingState;
  longTermShiftContractId?: string;
  date?: string;
}
interface IUseNotifications {
  closeNotification: () => void;
  handleMarkAsRead?: (id: string) => void;
}

const useNotification = ({
  closeNotification,
  handleMarkAsRead,
}: IUseNotifications) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { openDrawer } = useDrawerNavigation();
  const { isSelfService } = useSelfService();
  const visitSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        notificationActions.getNotification,
        notificationActions.getNotificationLoad,
      ],
      state,
    ));

  const userType = useAppSelector(getTypeOfUser);
  const notificationState = useAppSelector(getNotificationState);
  const clickedNotification = useAppSelector(getClickedNotification);
  const isPharmacy = isSelfService && userType === pharmacy;
  const isPharmacist = userType === pharmacist;

  const commonDrawerOpen = ({
    id,
    inBookingState,
    inType,
    isAutoSyncRecord,
    longTermShiftContractId,
    date,
    professionalId,
  }: ICommonDrawerOpen) => {
    openDrawer({
      eventIds: [id],
      type: inType,
      bookingState: inBookingState,
      recurrenceType: series,
      date,
      recurrenceId: '',
      fromNotification: true,
      isAutoSyncIDMap: { [id]: !!isAutoSyncRecord },
      longTermShiftContractId,
      professionalId,
    });
  };

  // NOTIFICATIONS CAN CARRY THEIR OWN DESTINATION INSTEAD OF A SHIFT DRAWER
  const openNavigationUrl = (navigation: string) => {
    if (isAppLandingUrl(navigation)) {
      navigate(calendarDashboard);
      dispatch(setInitialLandingURL(navigation));
      return;
    }

    globalThis.open(navigation, '_blank', 'noopener,noreferrer');
  };

  const visitNotifications = (notificationItem: INotificationsData) => {
    const {
      notificationId: id,
      notificationType,
      navigation,
    } = notificationItem;
    const { iconType } = NotificationIdMap[notificationType] ?? {};

    if (iconType === PositiveIcons.cancel) {
      dispatch(getNotification(id));
    } else {
      dispatch(getNotificationLoad(id));
    }

    handleMarkAsRead?.(id);

    if (navigation) {
      closeNotification?.();
      openNavigationUrl(navigation);
      return;
    }

    dispatch(
      setClickedNotification({ isRequested: true, item: notificationItem }),
    );
  };

  const { startDate, notificationType: clickedNotificationType } = clickedNotification.item
    ?? { startDate: '', notificationType: null };
  const isRequestedShift = clickedNotificationType === NotificationsType.RequestedShift;
  const isPastDate = startDate < getTodayWithoutOffset();

  const handleNotification = () => {
    const {
      bookingId,
      bookingState,
      status,
      workshiftId,
      isAccessible,
      professionalId,
      isAutoSyncRecord,
      longTermShiftContractId,
    } = notificationState;
    const isPendingPharmacist = isPharmacist;

    const type = statusMap[status as keyof typeof statusMap];
    const isAutoSyncBool = !!isAutoSyncRecord;

    const openRequestedBookingDrawer = () => {
      openDrawer({
        eventIds: [bookingId],
        type: requested,
        bookingState,
        fromNotification: true,
        isAutoSyncIDMap: { [bookingId]: isAutoSyncBool },
        longTermShiftContractId,
        professionalId,
      });
    };

    if (isPastDate || !isAccessible) return;

    closeNotification?.();
    navigate(calendarDashboard);

    // A REQUESTED SHIFT IS IDENTIFIED BY ITS BOOKING, NOT ITS WORKSHIFT
    if (isRequestedShift) {
      openRequestedBookingDrawer();
      return;
    }

    switch (type) {
      case posted:
        if (isPharmacy) {
          commonDrawerOpen({
            id: workshiftId,
            inBookingState: bookingState,
            inType: type,
            isAutoSyncRecord,
            longTermShiftContractId,
          });
        } else if (isPharmacist) {
          openDrawer({
            eventIds: [workshiftId],
            type: PharmacistLegendVariant.workshift,
            isAutoSyncIDMap: { [workshiftId]: isAutoSyncBool },
            longTermShiftContractId,
          });
        }
        break;
      case requested:
        if (isPendingPharmacist) {
          openRequestedBookingDrawer();
        } else {
          commonDrawerOpen({
            id: isSelfService ? bookingId : workshiftId,
            inBookingState: bookingState,
            date: startDate,
            inType: type,
            isAutoSyncRecord,
            longTermShiftContractId,
            professionalId,
          });
        }
        return;
      case pharmacyBooked:
        openDrawer({
          eventIds: [bookingId],
          type,
          date: startDate,
          professionalId,
          bookingState,
          fromNotification: true,
          longTermShiftContractId,
          isAutoSyncIDMap: { [bookingId]: isAutoSyncBool },
        });
        break;
      default:
        openDrawer({
          eventIds: [bookingId],
          type: type as PharmacistLegendVariant,
          bookingState,
          isAutoSyncIDMap: { [bookingId]: isAutoSyncBool },
          longTermShiftContractId,
        });
        break;
    }
  };

  useEffect(() => {
    if (visitSuccess) {
      dispatch(
        resetStatus([
          notificationActions.getNotification,
          notificationActions.getNotificationLoad,
        ]),
      );
    }
    if (visitSuccess && clickedNotification.isRequested) {
      dispatch(setClickedNotification(initValue));
      handleNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitSuccess]);

  return { handleActions: visitNotifications };
};

export default useNotification;
